import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, Download, FileSpreadsheet, FileText, Heart, Waves } from 'lucide-react'
import { downloadFromEdgeFunction } from '../../../../lib/edgeFunctions'
import { lireSousGroupes, type SousGroupe } from '../../../../lib/groupes/groupes.supabase'
import { nomDeFichier, type Filtre, type SessionGroupe } from '../../../../lib/groupes/sessions'
import { FiltresSessions } from './FiltresSessions'
import { useSessionsGroupes } from './useSessionsGroupes'

type Mode = 'presence' | 'health' | 'both'

const MODES: { valeur: Mode; libelle: string }[] = [
  { valeur: 'presence', libelle: 'Présences' },
  { valeur: 'health', libelle: 'Santé (inclusion et droit à l’image compris)' },
  { valeur: 'both', libelle: 'Présences + santé' },
]

/**
 * Au-delà, le PDF groupé risque de dépasser la durée d'exécution d'une
 * fonction : chaque session coûte deux à quatre listes. Un centre pour une
 * semaine en compte cinq ou six.
 */
const SESSIONS_PAR_DOCUMENT = 20

type Message = { ton: 'erreur' | 'alerte'; texte: string }

const bouton =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * Les listes à imprimer avant le stage : présences, santé (avec l'inclusion
 * et le droit à l'image), natation. Ce module ne modifie rien.
 */
export function ListingsModule({ onBack, filtreInitial }: { onBack: () => void; filtreInitial?: Partial<Filtre> }) {
  const etat = useSessionsGroupes(filtreInitial)
  const { filtrees, chargement, erreur } = etat

  const ids = useMemo(() => filtrees.map((s) => s.id), [filtrees])
  const [sousGroupes, setSousGroupes] = useState<Map<string, SousGroupe[]>>(new Map())
  const [selection, setSelection] = useState<Set<string>>(new Set())
  const [mode, setMode] = useState<Mode>('both')
  const [enCours, setEnCours] = useState<string | null>(null)
  const [message, setMessage] = useState<Message | null>(null)

  useEffect(() => {
    setSelection(new Set(ids))
    if (ids.length === 0) {
      setSousGroupes(new Map())
      return
    }
    let actif = true
    lireSousGroupes(ids)
      .then((groupes) => {
        if (!actif) return
        const parSession = new Map<string, SousGroupe[]>()
        for (const g of groupes) parSession.set(g.sessionId, [...(parSession.get(g.sessionId) ?? []), g])
        setSousGroupes(parSession)
      })
      .catch(() => {
        if (actif) setSousGroupes(new Map())
      })
    return () => {
      actif = false
    }
  }, [ids])

  async function telecharger(cle: string, fonction: string, corps: unknown, fichier: string) {
    setEnCours(cle)
    setMessage(null)
    try {
      const entetes = await downloadFromEdgeFunction(fonction, corps, fichier)
      const manquantes = Number(entetes.get('X-Listes-Manquantes') ?? 0)
      if (manquantes > 0) {
        setMessage({
          ton: 'alerte',
          texte: `Document incomplet : ${manquantes} liste(s) n’ont pas pu être générées. Elles sont nommées sur la première page du PDF ; relancez le téléchargement ou imprimez-les une par une.`,
        })
      }
    } catch (e) {
      setMessage({ ton: 'erreur', texte: e instanceof Error ? e.message : 'Le téléchargement a échoué.' })
    } finally {
      setEnCours(null)
    }
  }

  const listes = (s: SessionGroupe, m: Mode, suffixe: string) =>
    telecharger(
      `${m}-${s.id}`,
      'generate-week-pdf-bundle',
      { session_ids: [s.id], mode: m },
      `${nomDeFichier(s.semaine, s.stage, s.centre, suffixe)}.pdf`,
    )

  const choisies = filtrees.filter((s) => selection.has(s.id))
  const tropDeSessions = choisies.length > SESSIONS_PAR_DOCUMENT
  const premiere = choisies[0]

  const basculer = (id: string) =>
    setSelection((avant) => {
      const apres = new Set(avant)
      if (apres.has(id)) apres.delete(id)
      else apres.add(id)
      return apres
    })

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Listings</h1>
          <p className="text-gray-600">Présences, fiches santé et natation à imprimer avant le stage</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <FiltresSessions etat={etat} />
        {erreur && <p className="text-sm text-red-700">{erreur}</p>}
      </div>

      {message && (
        <div
          role="alert"
          className={`flex items-start gap-2 rounded-lg border p-4 text-sm ${
            message.ton === 'erreur' ? 'border-red-300 bg-red-50 text-red-800' : 'border-amber-300 bg-amber-50 text-amber-900'
          }`}
        >
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{message.texte}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center gap-4 justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Sessions ({filtrees.length})
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              aria-label="Contenu du document"
            >
              {MODES.map((m) => (
                <option key={m.valeur} value={m.valeur}>
                  {m.libelle}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!premiere || tropDeSessions || enCours !== null}
              onClick={() =>
                premiere &&
                telecharger(
                  'lot',
                  'generate-week-pdf-bundle',
                  { session_ids: choisies.map((s) => s.id), mode },
                  `${nomDeFichier(premiere.periode, premiere.semaine, etat.filtre.centreId ? premiere.centre : 'tous-centres', mode === 'presence' ? 'presences' : mode === 'health' ? 'sante' : 'presences-sante')}.pdf`,
                )
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white"
            >
              <Download className="h-4 w-4" />
              {enCours === 'lot' ? 'Génération en cours…' : `Télécharger la sélection (${choisies.length})`}
            </button>
          </div>
          {tropDeSessions && (
            <p className="w-full text-xs text-amber-800">
              {SESSIONS_PAR_DOCUMENT} sessions au plus par document : choisissez un centre, ou décochez des sessions.
            </p>
          )}
        </div>

        {chargement ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filtrees.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-gray-500">Aucune session pour ces filtres.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtrees.map((s) => {
              const groupes = sousGroupes.get(s.id) ?? []
              const occupe = enCours !== null
              return (
                <li key={s.id} className="px-6 py-4">
                  <div className="flex flex-wrap items-start gap-3 justify-between">
                    <label className="flex items-start gap-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={selection.has(s.id)}
                        onChange={() => basculer(s.id)}
                        className="mt-1 h-4 w-4"
                      />
                      <span className="min-w-0">
                        <span className="block font-medium text-gray-900">{s.stage}</span>
                        <span className="block text-sm text-gray-500">
                          {s.centre} · {s.periode} {s.semaine} · {s.inscrits} inscrit(s)
                          {groupes.length > 0 && ` · ${groupes.length} sous-groupe(s)`}
                        </span>
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={bouton} disabled={occupe} onClick={() => listes(s, 'presence', 'presences')}>
                        <FileText className="h-3.5 w-3.5" />
                        {enCours === `presence-${s.id}` ? '…' : 'Présences'}
                      </button>
                      <button type="button" className={bouton} disabled={occupe} onClick={() => listes(s, 'health', 'sante')}>
                        <Heart className="h-3.5 w-3.5" />
                        {enCours === `health-${s.id}` ? '…' : 'Santé'}
                      </button>
                      <button
                        type="button"
                        className={bouton}
                        disabled={occupe}
                        onClick={() =>
                          telecharger(
                            `natation-${s.id}`,
                            'generate-swimming-info-pdf',
                            { session_id: s.id },
                            `${nomDeFichier(s.semaine, s.stage, s.centre, 'natation')}.pdf`,
                          )
                        }
                      >
                        <Waves className="h-3.5 w-3.5" />
                        {enCours === `natation-${s.id}` ? '…' : 'Natation'}
                      </button>
                      {groupes.length === 0 && (
                        <button
                          type="button"
                          className={bouton}
                          disabled={occupe}
                          onClick={() =>
                            telecharger(
                              `csv-${s.id}`,
                              'generate-unassigned-presence-csv',
                              { session_id: s.id },
                              `${nomDeFichier(s.semaine, s.stage, s.centre, 'presences')}.csv`,
                            )
                          }
                        >
                          <FileSpreadsheet className="h-3.5 w-3.5" />
                          {enCours === `csv-${s.id}` ? '…' : 'CSV'}
                        </button>
                      )}
                    </div>
                  </div>

                  {groupes.length > 0 && (
                    <div className="mt-2 ml-7 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                      <span>CSV par groupe :</span>
                      {groupes.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          className={bouton}
                          disabled={occupe}
                          onClick={() =>
                            telecharger(
                              `csv-${g.id}`,
                              'generate-subgroup-presence-csv',
                              { subgroup_id: g.id },
                              `${nomDeFichier(s.semaine, s.stage, g.nom, 'presences')}.csv`,
                            )
                          }
                        >
                          {enCours === `csv-${g.id}` ? '…' : `${g.nom} (${g.effectif})`}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={bouton}
                        disabled={occupe}
                        onClick={() =>
                          telecharger(
                            `csv-${s.id}`,
                            'generate-unassigned-presence-csv',
                            { session_id: s.id },
                            `${nomDeFichier(s.semaine, s.stage, 'non-assignes', 'presences')}.csv`,
                          )
                        }
                      >
                        {enCours === `csv-${s.id}` ? '…' : 'Non assignés'}
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
